export type AppEnvName = "production" | "qa" | "local" | "unset";

export type RuntimeEnv = {
  name: AppEnvName;
  misconfigured: boolean;
  banner: string | null;
};

declare const __LIFT_VERCEL_ENV__: string;

const HOSTED_ENVS = new Set(["production", "qa", "local"]);
const DEBUG_ON = new Set(["", "1", "true", "yes", "on"]);

export function isLocalHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local")
  );
}

/** Laptop dev: unset env on localhost is treated as local. */
export function effectiveAppEnv(
  envName: AppEnvName,
  hostname: string,
): AppEnvName {
  if (envName === "unset" && isLocalHostname(hostname)) return "local";
  return envName;
}

export function resolveRuntimeEnv(
  appEnv: string | undefined,
  hostname: string,
  vercelEnv = "",
): RuntimeEnv {
  const raw = (appEnv ?? "").trim().toLowerCase();
  let name: AppEnvName = HOSTED_ENVS.has(raw) ? (raw as AppEnvName) : "unset";
  const host = hostname.trim().toLowerCase();
  name = effectiveAppEnv(name, host);
  const preview = vercelEnv === "preview" || vercelEnv === "development";
  const obviousNonProd =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.startsWith("qa.") ||
    host.endsWith(".local");
  const misconfigured = name === "production" && (preview || obviousNonProd);

  if (misconfigured) {
    return { name, misconfigured: true, banner: "NOT PRODUCTION" };
  }
  if (name === "qa") {
    return { name, misconfigured: false, banner: "QA" };
  }
  if (name === "local") {
    return { name, misconfigured: false, banner: "LOCAL" };
  }
  if (name === "unset") {
    return { name: "unset", misconfigured: false, banner: "ENVIRONMENT NOT SET" };
  }
  return { name: "production", misconfigured: false, banner: null };
}

/** True when the address includes `debug` as 1, true, yes, on, or an empty value. */
export function debugQueryEnabled(search: string): boolean {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(query);
  if (!params.has("debug")) return false;
  return DEBUG_ON.has((params.get("debug") ?? "").trim().toLowerCase());
}

function isHostedDeployment(hostname: string, envName: AppEnvName): boolean {
  const effective = effectiveAppEnv(envName, hostname);
  if (effective === "qa" || effective === "production") return true;
  const host = hostname.trim().toLowerCase();
  return host.startsWith("qa.") || host === "app.liftteencenter.com";
}

/**
 * One-click dev sign-ins on localhost (VITE_APP_ENV=local, QA Supabase in .env.local).
 * Never on hosted QA or production.
 */
export function localDevShortcutsEnabled(
  envName: AppEnvName,
  search: string,
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
): boolean {
  void search;
  const host = hostname.trim().toLowerCase();
  if (isHostedDeployment(host, envName)) return false;
  return effectiveAppEnv(envName, host) === "local" && isLocalHostname(host);
}

/**
 * Random-student form fill. Local and QA only — never production.
 * Unset env on localhost counts as local.
 */
export function sampleToolsEnabled(
  envName: AppEnvName,
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
): boolean {
  const name = effectiveAppEnv(envName, hostname);
  return name === "local" || name === "qa";
}

/** @deprecated Use localDevShortcutsEnabled */
export function demoAccountsEnabled(
  envName: AppEnvName,
  search: string,
  hostname?: string,
): boolean {
  return localDevShortcutsEnabled(envName, search, hostname);
}

/** Keep a debug query on a path, and add any extra parameters. */
export function pathKeepingDebug(
  pathname: string,
  search: string,
  extra: Record<string, string> = {},
): string {
  const current = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const next = new URLSearchParams();
  if (debugQueryEnabled(search)) next.set("debug", current.get("debug") ?? "");
  for (const [key, value] of Object.entries(extra)) next.set(key, value);
  const text = next.toString();
  return text ? `${pathname}?${text}` : pathname;
}

export function runtimeFromBrowser(): RuntimeEnv {
  const hostname = typeof window === "undefined" ? "" : window.location.hostname;
  const vercelEnv =
    typeof __LIFT_VERCEL_ENV__ === "string" ? __LIFT_VERCEL_ENV__ : "";
  return resolveRuntimeEnv(import.meta.env.VITE_APP_ENV, hostname, vercelEnv);
}

/** Public Supabase settings for the browser. The service role key is intentionally absent. */
export function publicSupabaseConfig(
  env: { url?: string; anonKey?: string } = {
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  },
): { url: string; anonKey: string } | null {
  const url = env.url?.trim() ?? "";
  const anonKey = env.anonKey?.trim() ?? "";
  if (!url || !anonKey) return null;
  return { url, anonKey };
}
